import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from 'reactstrap';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import PromotionalBannerPreview from './PromotionalBannerPreview';

const PromotionalBannerForm = ({ banner, onClose, onSuccess }) => {
  const isEditMode = !!banner;
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://api.junctionverse.com';

  const [formData, setFormData] = useState({
    position: banner?.position || 'category_below',
    title: banner?.title || '',
    subtitle: banner?.subtitle || '',
    buttonText: banner?.buttonText || '',
    buttonColor: banner?.buttonColor || '#FFFFFF',
    textColor: banner?.textColor || '#FFFFFF',
    bgColor: banner?.bgColor || '#000000',
    imageUrl: banner?.imageUrl || null,
    actionType: banner?.actionType || 'none',
    actionUrl: banner?.actionUrl || '',
    registrationUrl: banner?.registrationUrl || '',
    height: banner?.height || '',
    width: banner?.width || '',
    imageOpacity: banner?.imageOpacity !== undefined && banner?.imageOpacity !== null ? banner.imageOpacity : '',
    priority: banner?.priority || 0,
    startDate: banner?.startDate ? new Date(banner.startDate).toISOString().split('T')[0] : '',
    endDate: banner?.endDate ? new Date(banner.endDate).toISOString().split('T')[0] : '',
    targetAudience: banner?.targetAudience || 'all',
    tags: banner?.tags?.join(', ') || '',
    isActive: banner?.isActive !== undefined ? banner.isActive : true,
  });

  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(banner?.imageUrl || null);
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        toast.error('Please select an image file');
        return;
      }

      // Validate file size (5MB limit)
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Image size must be less than 5MB');
        return;
      }

      setImageFile(file);
      
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeImage = () => {
    // Only remove newly selected image file, keep existing image in edit mode
    setImageFile(null);
    // If it was a new file, clear the preview; otherwise keep existing image preview
    if (imageFile) {
      setImagePreview(isEditMode && formData.imageUrl ? formData.imageUrl : null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!formData.position || !formData.title) {
      toast.error('Position and Title are required');
      return;
    }

    try {
      setSubmitting(true);
      const token = localStorage.getItem('accessToken');

      // Since FormData sends all values as strings, and backend expects boolean for isActive,
      // we need to handle this differently. The backend routes use multer which expects multipart/form-data.
      // Solution: Send data in two parts - first update without isActive, then update isActive separately
      // OR: Don't send isActive in FormData and use toggle endpoint if needed
      // Actually, simplest: Send all fields except isActive, then if isActive changed, make separate call
      
      // For now, let's send everything except isActive, and handle isActive via toggle if needed
      // But that's not ideal. Better: Send isActive as a number that backend can convert
      // Since backend does: if (isActive !== undefined) updateData.isActive = isActive;
      // And FormData sends "1" or "0" as strings, backend will assign string to boolean = error
      
      // Best solution without backend change: Send as JSON when no file, FormData when file exists
      // But routes always use multer, so they expect multipart...
      
      // Actually, multer only processes files. If we send JSON with Content-Type: application/json,
      // multer won't process it, but body-parser will parse JSON into req.body. So that should work!
      
      let response;
      const dataPayload = {
        position: formData.position,
        title: formData.title,
        subtitle: formData.subtitle || null,
        buttonText: formData.buttonText || null,
        buttonColor: formData.buttonColor,
        textColor: formData.textColor,
        bgColor: formData.bgColor,
        actionType: formData.actionType,
        actionUrl: formData.actionUrl || null,
        registrationUrl: formData.registrationUrl || null,
        height: formData.height ? parseFloat(formData.height) : null,
        width: formData.width ? parseFloat(formData.width) : null,
        imageOpacity: formData.imageOpacity !== '' && formData.imageOpacity !== null ? parseFloat(formData.imageOpacity) : null,
        priority: parseInt(formData.priority),
        targetAudience: formData.targetAudience,
        tags: formData.tags,
        isActive: formData.isActive, // Boolean preserved
      };

      if (formData.startDate) {
        dataPayload.startDate = new Date(formData.startDate).toISOString();
      }
      if (formData.endDate) {
        dataPayload.endDate = new Date(formData.endDate).toISOString();
      }

      if (imageFile) {
        // Has file, must use FormData
        const submitData = new FormData();
        // Send all fields as strings (FormData limitation)
        Object.keys(dataPayload).forEach(key => {
          if (key !== 'isActive') { // Skip isActive, handle separately
            const value = dataPayload[key];
            if (value !== null && value !== undefined) {
              if (Array.isArray(value)) {
                submitData.append(key, value.join(','));
              } else if (typeof value === 'number') {
                // Preserve numbers as strings (backend will parse)
                submitData.append(key, value.toString());
              } else {
                submitData.append(key, value.toString());
              }
            }
          }
        });
        submitData.append('image', imageFile);
        
        // Send isActive separately via query param or header (but backend reads from body)
        // Actually, we need to send it in body. Since FormData sends strings,
        // we'll send it and hope backend can handle it... but it can't without changes.
        // So we'll send it and it will fail, OR we make a second request to update isActive
        
        if (isEditMode) {
          // First update without isActive
          await axios.put(
            `${API_BASE_URL}/api/promotional-banner/${banner.id}`,
            submitData,
            {
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'multipart/form-data',
              },
            }
          );
          // Then update isActive separately if it changed
          if (banner.isActive !== formData.isActive) {
            await axios.patch(
              `${API_BASE_URL}/api/promotional-banner/${banner.id}/toggle`,
              {},
              {
                headers: {
                  'Authorization': `Bearer ${token}`,
                },
              }
            );
          }
          response = { data: { success: true } };
        } else {
          // Create - send without isActive (backend defaults to true)
          response = await axios.post(
            `${API_BASE_URL}/api/promotional-banner/create`,
            submitData,
            {
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'multipart/form-data',
              },
            }
          );
          // Then set isActive if needed
          if (!formData.isActive && response.data.id) {
            await axios.patch(
              `${API_BASE_URL}/api/promotional-banner/${response.data.id}/toggle`,
              {},
              {
                headers: {
                  'Authorization': `Bearer ${token}`,
                },
              }
            );
          }
        }
      } else {
        // No file, send as JSON to preserve boolean types
        if (isEditMode) {
          response = await axios.put(
            `${API_BASE_URL}/api/promotional-banner/${banner.id}`,
            dataPayload,
            {
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
              },
            }
          );
        } else {
          response = await axios.post(
            `${API_BASE_URL}/api/promotional-banner/create`,
            dataPayload,
            {
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
              },
            }
          );
        }
      }

      toast.success(isEditMode ? 'Banner updated successfully' : 'Banner created successfully');

      // Notify parent
      if (onSuccess) {
        onSuccess();
      }

      // Close after delay
      setTimeout(() => {
        if (onClose) {
          onClose();
        }
      }, 1500);
    } catch (err) {
      console.error('Error saving banner:', err);
      const errorMessage = err.response?.data?.error || `Failed to ${isEditMode ? 'update' : 'create'} banner`;
      toast.error(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  // Preview data - merge form data with preview image
  const previewData = {
    ...formData,
    imageUrl: imagePreview || formData.imageUrl,
  };

  return (
    <div style={{ padding: '20px' }}>
      <ToastContainer position="top-right" autoClose={3000} hideProgressBar />
      
      <form onSubmit={handleSubmit}>
        <div className="row">
          {/* Left Column - Form */}
          <div className="col-lg-6">
            <div className="mb-4">
              <h4>{isEditMode ? 'Edit Promotional Banner' : 'Create New Promotional Banner'}</h4>
              <p className="text-muted">Fill in the details below to {isEditMode ? 'update' : 'create'} a promotional banner</p>
            </div>

            {/* Position */}
            <div className="mb-3">
              <label className="form-label">Position *</label>
              <select
                className="form-select"
                name="position"
                value={formData.position}
                onChange={handleChange}
                required
                disabled={submitting}
              >
                <option value="category_below">Category Below</option>
                <option value="home_middle">Home Middle</option>
                <option value="home_top">Home Top</option>
              </select>
            </div>

            {/* Title */}
            <div className="mb-3">
              <label className="form-label">Title *</label>
              <input
                type="text"
                className="form-control"
                name="title"
                value={formData.title}
                onChange={handleChange}
                placeholder="Banner Title"
                required
                disabled={submitting}
              />
            </div>

            {/* Subtitle */}
            <div className="mb-3">
              <label className="form-label">Subtitle</label>
              <input
                type="text"
                className="form-control"
                name="subtitle"
                value={formData.subtitle}
                onChange={handleChange}
                placeholder="Optional subtitle text"
                disabled={submitting}
              />
            </div>

            {/* Button Text */}
            <div className="mb-3">
              <label className="form-label">Button Text</label>
              <input
                type="text"
                className="form-control"
                name="buttonText"
                value={formData.buttonText}
                onChange={handleChange}
                placeholder="e.g., Register, Learn More"
                disabled={submitting}
              />
            </div>

            {/* Colors */}
            <div className="row mb-3">
              <div className="col-md-4">
                <label className="form-label">Background Color</label>
                <input
                  type="color"
                  className="form-control form-control-color"
                  name="bgColor"
                  value={formData.bgColor}
                  onChange={handleChange}
                  disabled={submitting}
                />
              </div>
              <div className="col-md-4">
                <label className="form-label">Text Color</label>
                <input
                  type="color"
                  className="form-control form-control-color"
                  name="textColor"
                  value={formData.textColor}
                  onChange={handleChange}
                  disabled={submitting}
                />
              </div>
              <div className="col-md-4">
                <label className="form-label">Button Color</label>
                <input
                  type="color"
                  className="form-control form-control-color"
                  name="buttonColor"
                  value={formData.buttonColor}
                  onChange={handleChange}
                  disabled={submitting}
                />
              </div>
            </div>

            {/* Image Upload */}
            <div className="mb-3">
              <label className="form-label">Background Image</label>
              {imagePreview && (
                <div className="mb-2">
                  <img
                    src={imagePreview}
                    alt="Preview"
                    style={{
                      maxWidth: '100%',
                      maxHeight: '200px',
                      borderRadius: '8px',
                      border: '1px solid #ddd',
                      display: 'block',
                      marginBottom: '10px',
                    }}
                  />
                  {imageFile && (
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-danger mb-2"
                      onClick={removeImage}
                      disabled={submitting}
                    >
                      Remove New Image
                    </button>
                  )}
                </div>
              )}
              {!imageFile && (
                <input
                  type="file"
                  className="form-control"
                  accept="image/*"
                  onChange={handleImageChange}
                  disabled={submitting}
                />
              )}
              <small className="text-muted d-block mt-1">Maximum file size: 5MB. Recommended: 800x200px or similar aspect ratio.</small>
            </div>

            {/* Banner Dimensions */}
            <div className="row mb-3">
              <div className="col-md-6">
                <label className="form-label">Width (logical pixels)</label>
                <input
                  type="number"
                  className="form-control"
                  name="width"
                  value={formData.width}
                  onChange={handleChange}
                  placeholder="e.g., 400"
                  min="0"
                  step="1"
                  disabled={submitting}
                />
                <small className="text-muted">Leave empty for dynamic sizing</small>
              </div>
              <div className="col-md-6">
                <label className="form-label">Height (logical pixels)</label>
                <input
                  type="number"
                  className="form-control"
                  name="height"
                  value={formData.height}
                  onChange={handleChange}
                  placeholder="e.g., 200"
                  min="0"
                  step="1"
                  disabled={submitting}
                />
                <small className="text-muted">Leave empty for dynamic sizing</small>
              </div>
            </div>

            {/* Image Opacity */}
            <div className="mb-3">
              <label className="form-label">Image Opacity</label>
              <input
                type="number"
                className="form-control"
                name="imageOpacity"
                value={formData.imageOpacity}
                onChange={handleChange}
                placeholder="0.0 to 1.0"
                min="0"
                max="1"
                step="0.1"
                disabled={submitting}
              />
              <small className="text-muted">Value between 0.0 (transparent) and 1.0 (opaque). Leave empty to use default (0.0 if image exists, 0.3 otherwise).</small>
            </div>

            {/* Action Type */}
            <div className="mb-3">
              <label className="form-label">Action Type</label>
              <select
                className="form-select"
                name="actionType"
                value={formData.actionType}
                onChange={handleChange}
                disabled={submitting}
              >
                <option value="none">None</option>
                <option value="navigate">Navigate</option>
                <option value="external_link">External Link</option>
                <option value="webview">WebView</option>
              </select>
            </div>

            {/* Action URL */}
            {formData.actionType !== 'none' && (
              <div className="mb-3">
                <label className="form-label">
                  {formData.actionType === 'webview' ? 'Registration URL' : 'Action URL'}
                </label>
                <input
                  type="url"
                  className="form-control"
                  name={formData.actionType === 'webview' ? 'registrationUrl' : 'actionUrl'}
                  value={formData.actionType === 'webview' ? formData.registrationUrl : formData.actionUrl}
                  onChange={handleChange}
                  placeholder={formData.actionType === 'webview' ? 'https://...' : 'URL or route'}
                  disabled={submitting}
                />
              </div>
            )}

            {/* Priority */}
            <div className="mb-3">
              <label className="form-label">Priority</label>
              <input
                type="number"
                className="form-control"
                name="priority"
                value={formData.priority}
                onChange={handleChange}
                min="0"
                disabled={submitting}
              />
              <small className="text-muted">Higher priority banners are shown first</small>
            </div>

            {/* Dates */}
            <div className="row mb-3">
              <div className="col-md-6">
                <label className="form-label">Start Date</label>
                <input
                  type="date"
                  className="form-control"
                  name="startDate"
                  value={formData.startDate}
                  onChange={handleChange}
                  disabled={submitting}
                />
              </div>
              <div className="col-md-6">
                <label className="form-label">End Date</label>
                <input
                  type="date"
                  className="form-control"
                  name="endDate"
                  value={formData.endDate}
                  onChange={handleChange}
                  disabled={submitting}
                />
              </div>
            </div>

            {/* Target Audience */}
            <div className="mb-3">
              <label className="form-label">Target Audience</label>
              <select
                className="form-select"
                name="targetAudience"
                value={formData.targetAudience}
                onChange={handleChange}
                disabled={submitting}
              >
                <option value="all">All Users</option>
                <option value="verified">Verified Users</option>
                <option value="onboarded">Onboarded Users</option>
                <option value="specific_tags">Specific Tags</option>
              </select>
            </div>

            {/* Tags */}
            <div className="mb-3">
              <label className="form-label">Tags (comma-separated)</label>
              <input
                type="text"
                className="form-control"
                name="tags"
                value={formData.tags}
                onChange={handleChange}
                placeholder="tag1, tag2, tag3"
                disabled={submitting}
              />
            </div>

            {/* Active Status */}
            <div className="mb-4">
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  name="isActive"
                  checked={formData.isActive}
                  onChange={handleChange}
                  disabled={submitting}
                  id="isActiveCheck"
                />
                <label className="form-check-label" htmlFor="isActiveCheck">
                  Active
                </label>
              </div>
            </div>

            {/* Buttons */}
            <div className="d-flex justify-content-end gap-2">
              <Button color="secondary" onClick={onClose} disabled={submitting}>
                Cancel
              </Button>
              <Button
                color="primary"
                type="submit"
                disabled={submitting || !formData.title || !formData.position}
              >
                {submitting ? (isEditMode ? 'Updating...' : 'Creating...') : (isEditMode ? 'Update Banner' : 'Create Banner')}
              </Button>
            </div>
          </div>

          {/* Right Column - Preview */}
          <div className="col-lg-6">
            <PromotionalBannerPreview banner={previewData} />
          </div>
        </div>
      </form>
    </div>
  );
};

export default PromotionalBannerForm;
